import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { fetchCompatibilityById, updateCompatibility } from "../services/compatibilityService";
import { unlockVaultManual } from "../services/matchService";
import Loader from "../child/Loder";
import { normalizeCompatibilityRecord, extractRecordFromInteractionResponse } from "./compatibilityRecordUtils";

const COMPATIBILITY_STATUSES = [
  "generated",
  "shown",
  "accepted",
  "rejected",
  "expired",
  "blocked",
  "unmatched",
  "matched",
];

const EditCompatibilityPageNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fromUser: "",
    toUser: "",
    compatibilityStatus: "",
    reason: "",
  });

  const [vaultData, setVaultData] = useState({
    userId: "",
    matchedUserId: "",
    unlockType: "tier",
    tier: "iceBreaker",
    permanent: false,
    reason: "",
  });

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("compatibility-status");
  const [vaultErrors, setVaultErrors] = useState({});
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setInitialLoaded(false);
        const res = await fetchCompatibilityById(id);
        const body = res?.data;
        const data = extractRecordFromInteractionResponse(res);
        if (data?._id) {
          const norm = normalizeCompatibilityRecord(data);
          setFormData({
            fromUser: norm.fromEmail || "N/A",
            toUser: norm.toEmail || "N/A",
            compatibilityStatus: data.compatibilityStatus || "",
            reason: data.reason || "",
          });
          setVaultData((prev) => ({
            ...prev,
            userId: norm.fromUserId || "",
            matchedUserId: norm.toUserId || "",
          }));
        } else {
          setErrorMessage(body?.message || "Failed to fetch compatibility");
        }
      } catch (err) {
        setErrorMessage(
          err?.response?.data?.message || err?.message || "Failed to fetch compatibility"
        );
      } finally {
        setInitialLoaded(true);
      }
    };

    if (id) fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.reason || !formData.reason.trim()) {
      errors.reason = "Reason is required";
    } else if (formData.reason.trim().length < 5) {
      errors.reason = "Reason should be at least 5 characters";
    } else if (formData.reason.trim().length > 200) {
      errors.reason = "Reason cannot exceed 200 characters";
    }
    if (!formData.compatibilityStatus) {
      errors.compatibilityStatus = "Status is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const payload = {
        interactionId: id,
        status: formData.compatibilityStatus,
        compatibilityStatus: formData.compatibilityStatus,
        reason: formData.reason.trim() || "No reason provided",
      };

      const res = await updateCompatibility(payload);
      if (res?.data?.status === 1) {
        setSuccessMessage("Compatibility updated successfully!");
        setTimeout(() => navigate("/compatibility-control"), 1500);
      } else {
        setErrorMessage(res?.data?.message || "Failed to update compatibility");
      }
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || err?.message || "Error while updating compatibility"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleVaultChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVaultData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setVaultErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateVaultForm = () => {
    const errors = {};
    if (!vaultData.reason || !vaultData.reason.trim()) {
      errors.reason = "Reason is required";
    } else if (vaultData.reason.trim().length < 5) {
      errors.reason = "Reason should be at least 5 characters";
    } else if (vaultData.reason.trim().length > 200) {
      errors.reason = "Reason cannot exceed 200 characters";
    }
    if (vaultData.unlockType === "tier" && !vaultData.tier) {
      errors.tier = "Tier is required";
    }
    setVaultErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleVaultSubmit = async (e) => {
    e.preventDefault();
    if (!validateVaultForm()) return;
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const payload = {
        userId: vaultData.userId,
        matchedUserId: vaultData.matchedUserId,
        unlockType: vaultData.unlockType,
        tier: vaultData.unlockType === "tier" ? vaultData.tier : undefined,
        permanent: true,
        reason: vaultData.reason,
        unlockByAdmin: true,
      };

      const res = await unlockVaultManual(payload);
      if (res?.data?.status === 1) {
        setSuccessMessage("Vault unlocked successfully!");
        setTimeout(() => navigate("/compatibility-control"), 1500);
      } else {
        setErrorMessage(res?.data?.message || "Failed to unlock vault");
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Error while unlocking vault");
    } finally {
      setSaving(false);
    }
  };

  if (!initialLoaded) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-4">
        {successMessage && (
          <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />
        )}
        {errorMessage && (
          <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />
        )}
      </div>
      <div className="col-md-12">
        <div className="card h-100">
          <div className="card-body p-24">
            <ul
              className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
              id="compatibility-edit-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link d-flex align-items-center px-24 ${
                    activeTab === "compatibility-status" ? "active" : ""
                  }`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab("compatibility-status")}
                >
                  Compatibility Override
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link d-flex align-items-center px-24 ${
                    activeTab === "vault-unlock" ? "active" : ""
                  }`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab("vault-unlock")}
                >
                  Vault Unlock
                </button>
              </li>
            </ul>

            <div className="tab-content">
              <div
                className={`tab-pane fade ${
                  activeTab === "compatibility-status" ? "show active" : ""
                }`}
                role="tabpanel"
              >
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">From User</label>
                    <input type="text" className="form-control" value={formData.fromUser} disabled />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">To User</label>
                    <input type="text" className="form-control" value={formData.toUser} disabled />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Compatibility Status</label>
                    <select
                      className={`form-select ${formErrors.compatibilityStatus ? "is-invalid" : ""}`}
                      name="compatibilityStatus"
                      value={formData.compatibilityStatus}
                      onChange={handleChange}
                      required
                    >
                      <option value="" disabled>
                        Select status
                      </option>
                      {COMPATIBILITY_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </option>
                      ))}
                    </select>
                    {formErrors.compatibilityStatus && (
                      <div className="invalid-feedback d-block">{formErrors.compatibilityStatus}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Reason</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.reason ? "is-invalid" : ""}`}
                      name="reason"
                      value={formData.reason || ""}
                      onChange={handleChange}
                      placeholder="Enter reason for this admin change"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/\s{2,}/g, " ").trimStart();
                      }}
                    />
                    {formErrors.reason && (
                      <div className="invalid-feedback">{formErrors.reason}</div>
                    )}
                  </div>
                  <div className="d-flex justify-content-end">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Saving..." : "Update Compatibility"}
                    </button>
                  </div>
                </form>
              </div>

              <div
                className={`tab-pane fade ${activeTab === "vault-unlock" ? "show active" : ""}`}
                role="tabpanel"
              >
                <form onSubmit={handleVaultSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Unlock Type</label>
                    <select
                      name="unlockType"
                      value={vaultData.unlockType}
                      onChange={handleVaultChange}
                      className="form-select"
                    >
                      <option value="tier">Tier</option>
                      <option value="all">All Tiers</option>
                      <option value="reveal">Reveal Together</option>
                    </select>
                  </div>
                  {vaultData.unlockType === "tier" && (
                    <div className="mb-3">
                      <label className="form-label">Tier</label>
                      <select
                        name="tier"
                        value={vaultData.tier}
                        onChange={handleVaultChange}
                        className="form-select"
                      >
                        <option value="iceBreaker">Ice Breaker</option>
                        <option value="deeperDive">Deeper Dive</option>
                        <option value="wildCard">Wild Card</option>
                        <option value="connectionGoals">Connection Goals</option>
                      </select>
                    </div>
                  )}
                  {vaultData.unlockType === "reveal" && (
                    <div className="mb-3 form-check d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        name="permanent"
                        checked={vaultData.permanent}
                        onChange={handleVaultChange}
                      />
                      <label className="form-check-label ms-2">Permanent Unlock</label>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Reason</label>
                    <input
                      type="text"
                      className={`form-control ${vaultErrors.reason ? "is-invalid" : ""}`}
                      name="reason"
                      value={vaultData.reason}
                      onChange={handleVaultChange}
                      placeholder="Enter reason"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/\s{2,}/g, " ").trimStart();
                      }}
                    />
                    {vaultErrors.reason && (
                      <div className="invalid-feedback">{vaultErrors.reason}</div>
                    )}
                  </div>
                  <div className="d-flex justify-content-end">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Processing..." : "Vault Unlock"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCompatibilityPageNew;
